/**
 * Gemeinsame Bausteine der Verwaltungsseiten (Benutzer, Mein Konto, Protokoll, Einstellungen).
 */
import { useState, type ReactNode } from 'react';
import { Briefcase, Check, Copy, Crown, HelpCircle, Monitor, ShoppingCart, Smartphone, Tablet, Terminal } from 'lucide-react';
import { Badge, Button, useUi } from '../../components/ui';
import type { Permission, Role } from '../../lib/types';
import './admin.css';

// ---------------------------------------------------------------------------
// Typen der API (GET /users, /users/permissions, /auth/permissions, Sitzungen)
// ---------------------------------------------------------------------------
export interface AdminUser {
  id: number;
  username: string;
  displayName: string;
  role: Role;
  overrides: { grant: Permission[]; deny: Permission[] };
  permissions: Permission[];
  totpEnabled: boolean;
  mustChangePassword: boolean;
  active: boolean;
  lockedUntil: number | null;
  lastLoginAt: number | null;
  createdAt: number;
  activeSessions: number;
}

export interface PermissionInfo { key: Permission; group: string; label: string }
export interface RoleInfo { key: Role; label: string; description: string; permissions: Permission[] }
export interface PermissionCatalog { permissions: PermissionInfo[]; roles: RoleInfo[] }

/** Zeile aus GET /auth/sessions (eigene Geräte). `id` = erste 16 Zeichen der Sitzungs-ID. */
export interface MySession {
  id: string;
  created_at: number;
  last_seen_at: number;
  expires_at: number;
  ip: string | null;
  user_agent: string | null;
  current: boolean;
}

/** Zeile aus GET /users/sessions/all (alle Geräte aller Benutzer). */
export interface AdminSession {
  id: string;
  user_id: number;
  display_name: string;
  username: string;
  created_at: number;
  last_seen_at: number;
  expires_at: number;
  ip: string | null;
  user_agent: string | null;
}

// ---------------------------------------------------------------------------
// Rollen
// ---------------------------------------------------------------------------
export const ROLE_ORDER: Role[] = ['cashier', 'manager', 'admin'];

export const ROLE_TEXT: Record<Role, { label: string; description: string; icon: typeof Crown }> = {
  cashier: {
    label: 'Kassierer/in',
    description: 'Kassiert, öffnet und schließt die eigene Schicht, sieht Artikel und Bestand, stempelt die eigene Arbeitszeit, nimmt Pakete an, schreibt bei Stammkunden an und kassiert deren Schulden, gibt Online-Reservierungen aus (Abholung mit PIN/Abhol-Code). Zählt die Kasse blind – ohne den Soll-Betrag zu sehen.',
    icon: ShoppingCart,
  },
  manager: {
    label: 'Filialleitung',
    description: 'Alles fürs Tagesgeschäft: Kasse, Stornos und Rabatte, Artikel und Preise, Einkauf, Berichte mit Gewinn, Arbeitszeiten aller Mitarbeiter, Paketshop, Kunden & Schulden (Limits, Abschreiben) sowie der Online-Shop (Bestellungen, Artikel freischalten, Kunden sperren). Keine Benutzer, Einstellungen oder Datensicherung.',
    icon: Briefcase,
  },
  admin: {
    label: 'Administrator',
    description: 'Darf alles – auch Benutzer anlegen, Einstellungen ändern und Datensicherungen wiederherstellen. Nur für Inhaber/innen.',
    icon: Crown,
  },
};

export function RoleBadge({ role }: { role: Role }) {
  const r = ROLE_TEXT[role];
  const Icon = r.icon;
  return <Badge tone={role === 'admin' ? 'info' : undefined}><Icon size={12} aria-hidden /> {r.label}</Badge>;
}

/** Rollen-Standardrechte aus dem Katalog. */
export function roleDefaults(catalog: PermissionCatalog | undefined, role: Role): Set<Permission> {
  return new Set(catalog?.roles.find((r) => r.key === role)?.permissions ?? []);
}

/** Effektive Rechte wie auf dem Server (permissions.ts → effectivePermissions). */
export function computeEffective(defaults: Set<Permission>, role: Role, grant: Iterable<Permission>, deny: Iterable<Permission>): Set<Permission> {
  const s = new Set(defaults);
  for (const p of grant) s.add(p);
  for (const p of deny) s.delete(p);
  if (role === 'admin') s.add('users.manage');
  return s;
}

/** Rechte nach Gruppen (Reihenfolge wie im Katalog). */
export function groupPermissions<T extends { group: string }>(list: T[]): Array<{ group: string; items: T[] }> {
  const out: Array<{ group: string; items: T[] }> = [];
  for (const p of list) {
    let g = out.find((x) => x.group === p.group);
    if (!g) out.push((g = { group: p.group, items: [] }));
    g.items.push(p);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : parts[0][1] ?? '';
  return (first + last).toUpperCase();
}

export function Avatar({ name, size, muted }: { name: string; size?: 'lg'; muted?: boolean }) {
  return <span className={`adm-avatar${size === 'lg' ? ' lg' : ''}${muted ? ' off' : ''}`} aria-hidden>{initials(name)}</span>;
}

// ---------------------------------------------------------------------------
// Geräteerkennung aus dem User-Agent (grob, nur zur Anzeige)
// ---------------------------------------------------------------------------
export type DeviceKind = 'desktop' | 'mobile' | 'tablet' | 'script' | 'unknown';

export function parseUserAgent(ua: string | null | undefined): { label: string; kind: DeviceKind } {
  if (!ua) return { label: 'Unbekanntes Gerät', kind: 'unknown' };
  const s = ua;
  if (/^(curl|wget|python|node|undici|axios|go-http|java|okhttp|postman|insomnia)/i.test(s) || /supertest/i.test(s)) {
    const name = s.split(/[/\s]/)[0] || 'Programm';
    return { label: `Programm (${name})`, kind: 'script' };
  }

  let browser = 'Browser';
  if (/Edg(e|A|iOS)?\//.test(s)) browser = 'Edge';
  else if (/OPR\/|Opera/.test(s)) browser = 'Opera';
  else if (/SamsungBrowser\//.test(s)) browser = 'Samsung Internet';
  else if (/Firefox\/|FxiOS\//.test(s)) browser = 'Firefox';
  else if (/Chrome\/|CriOS\/|Chromium\//.test(s)) browser = 'Chrome';
  else if (/Safari\//.test(s) && /Version\//.test(s)) browser = 'Safari';
  else if (/HeadlessChrome/.test(s)) browser = 'Chrome';

  let os = '';
  let kind: DeviceKind = 'desktop';
  if (/iPhone|iPod/.test(s)) { os = 'iPhone'; kind = 'mobile'; }
  else if (/iPad/.test(s)) { os = 'iPad'; kind = 'tablet'; }
  else if (/Android/.test(s)) {
    const mobile = /Mobile/.test(s);
    os = mobile ? 'Android-Handy' : 'Android-Tablet';
    kind = mobile ? 'mobile' : 'tablet';
  } else if (/CrOS/.test(s)) os = 'Chromebook';
  else if (/Windows/.test(s)) os = 'Windows';
  else if (/Mac OS X|Macintosh/.test(s)) os = 'Mac';
  else if (/Linux/.test(s)) os = 'Linux';

  return { label: os ? `${browser} auf ${os}` : browser, kind };
}

export function DeviceIcon({ kind, size = 18 }: { kind: DeviceKind; size?: number }) {
  const Icon = kind === 'mobile' ? Smartphone : kind === 'tablet' ? Tablet : kind === 'script' ? Terminal : kind === 'unknown' ? HelpCircle : Monitor;
  return <Icon size={size} aria-hidden style={{ color: 'var(--text-3)', flex: 'none' }} />;
}

// ---------------------------------------------------------------------------
// Kopieren in die Zwischenablage (mit Rückfall für HTTP ohne Clipboard-API)
// ---------------------------------------------------------------------------
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* Rückfall unten */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

export function CopyButton({ text, label = 'Kopieren', size }: { text: string; label?: string; size?: 'sm' }) {
  const { toast } = useUi();
  const [done, setDone] = useState(false);
  return (
    <Button
      size={size}
      icon={done ? <Check size={16} /> : <Copy size={16} />}
      onClick={async () => {
        if (await copyText(text)) {
          setDone(true);
          toast('In die Zwischenablage kopiert', 'success');
          setTimeout(() => setDone(false), 2000);
        } else {
          toast('Kopieren nicht möglich – bitte von Hand abschreiben.', 'error');
        }
      }}
    >
      {done ? 'Kopiert' : label}
    </Button>
  );
}

/** Hervorgehobene Box für einmal angezeigte Passwörter/Schlüssel. */
export function SecretBox({ value, display, small, children }: { value: string; display?: string; small?: boolean; children?: ReactNode }) {
  return (
    <div className="stack tight">
      <div className={`adm-secret${small ? ' small' : ''}`}>
        <code aria-label="Wert">{display ?? value}</code>
        <CopyButton text={value} />
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sonstiges
// ---------------------------------------------------------------------------
export const isLocked = (u: Pick<AdminUser, 'lockedUntil'>, nowMs = Date.now()) => !!u.lockedUntil && u.lockedUntil > nowMs;

/** Mengen vergleichen (Reihenfolge egal). */
export function sameSet<T>(a: Iterable<T>, b: Iterable<T>): boolean {
  const sa = new Set(a), sb = new Set(b);
  if (sa.size !== sb.size) return false;
  for (const x of sa) if (!sb.has(x)) return false;
  return true;
}
